// Unpublished Work © 2020-2024 Deere & Company.

import React from 'react';
import appAccess from 'Common/constants/app-access';
import {IconMowingOrder, IconEquipmentTurf} from '@deere/icons';
import EditableSettings from 'Ui/components/settings/common/editable-settings';
import WorkboardSettings from 'Ui/components/settings/labor/workboard-settings';
import EditableWorkboardSettings from 'Ui/components/settings/labor/editable-workboard-settings';
import MyWorkSettings from 'Ui/components/settings/labor/my-work-settings';
import EditableMyWorkSettings from 'Ui/components/settings/labor/editable-my-work-settings';
import JobsSettings from 'Ui/components/settings/labor/jobs-settings';
import JobCategoriesSettings from 'Ui/components/settings/labor/job-categories-settings';
import EmployeeStatusesSettings from 'Ui/components/settings/labor/employee-statuses-settings';
import QuickTodoSettings from 'Ui/components/settings/labor/quick-todo-settings';
import MowingDirectionsSettings from 'Ui/components/settings/labor/mowing-directions-settings';
import BinLocationsSettings from 'Ui/pages/settings/fleet/bin-locations-settings';
import DealershipSettings from 'Ui/pages/settings/fleet/dealership-settings';
import ManufacturerSettings from 'Ui/pages/settings/fleet/manufacturer-settings';
import PartTypesSettings from 'Ui/pages/settings/fleet/part-types-settings';
import PartsSettings from 'Ui/pages/settings/fleet/parts-settings';
import MaintenanceSettings from 'Ui/pages/settings/fleet/maintenance-settings';
import {
    MANAGE_WORKBOARDS, MANAGE_EQUIPMENT_SETTINGS
} from 'Common/constants/business-activities';
import {isAuthorized} from 'Common/utils/authorization-handler';
import {isNullOrUndefined} from 'Common/utils/validation-utils';

const ICON_STYLE = {
    style: {
        height: '64px',
        width: '64px'
    }
};

function createSettingsTile({
    appAccess,
    dialogProps,
    icon,
    tileName,
    myJdPermissions
}) {
    return {
        appAccess,
        dialogProps,
        icon,
        myJdPermissions,
        name: tileName,
        tileId: tileName
    };
}

async function getSettingsTiles(translations, membership) {
    return [
        createSettingsTile({
            appAccess: appAccess.ONLABOR,
            dialogProps: {
                content: [
                    {
                        element: (
                            <EditableSettings
                                component={WorkboardSettings}
                                editableComponent={EditableWorkboardSettings}
                                id='Workboard'
                            />
                        ),
                        id: 'Workboard',
                        label: translations.ONLINK_WORKBOARD,
                        myJdPermissions: MANAGE_WORKBOARDS,
                        show: !membership.isMigrated
                    },
                    {
                        element: (
                            <EditableSettings
                                component={MyWorkSettings}
                                editableComponent={EditableMyWorkSettings}
                                id='MyWork'
                            />
                        ),
                        id: 'MyWork',
                        label: translations.ONLINK_MY_WORK,
                        myJdPermissions: MANAGE_WORKBOARDS,
                        show: !membership.isMigrated
                    },
                    {
                        element: <JobsSettings/>,
                        id: 'Jobs',
                        label: translations.JOBS,
                        myJdPermissions: MANAGE_WORKBOARDS
                    },
                    {
                        element: <JobCategoriesSettings/>,
                        id: 'JobCategories',
                        label: translations.ONLINK_JOB_CATEGORIES,
                        myJdPermissions: MANAGE_WORKBOARDS
                    },
                    {
                        element: <MowingDirectionsSettings/>,
                        id: 'MowingDirections',
                        label: translations.ONLINK_MOWING_DIRECTIONS,
                        myJdPermissions: MANAGE_WORKBOARDS
                    },
                    {
                        element: <QuickTodoSettings/>,
                        id: 'QuickTODOs',
                        label: translations.ONLINK_QUICK_TODOS,
                        myJdPermissions: MANAGE_WORKBOARDS
                    },
                    {
                        element: <EmployeeStatusesSettings/>,
                        id: 'Statuses',
                        label: translations.STATUSES,
                        myJdPermissions: MANAGE_WORKBOARDS
                    }
                ],
                title: translations.ONLINK_LABOR_SETTINGS
            },
            icon: <IconMowingOrder {...ICON_STYLE}/>,
            tileName: 'Labor'
        }),
        createSettingsTile({
            appAccess: appAccess.ONEQUIP,
            dialogProps: {
                content: [
                    {
                        element: <BinLocationsSettings/>,
                        id: 'BinLocations',
                        label: translations.ONLINK_BIN_LOCATIONS,
                        myJdPermissions: MANAGE_EQUIPMENT_SETTINGS
                    },
                    {
                        element: <DealershipSettings/>,
                        id: 'Dealerships',
                        label: translations.ONLINK_DEALERSHIPS,
                        myJdPermissions: MANAGE_EQUIPMENT_SETTINGS
                    },
                    {
                        element: <MaintenanceSettings/>,
                        id: 'Maintenance',
                        label: translations.ONLINK_MAINTENANCE,
                        myJdPermissions: MANAGE_EQUIPMENT_SETTINGS
                    },
                    {
                        element: <ManufacturerSettings/>,
                        id: 'Manufacturers',
                        label: translations.ONLINK_MANUFACTURERS,
                        myJdPermissions: MANAGE_EQUIPMENT_SETTINGS
                    },
                    {
                        element: <PartsSettings/>,
                        id: 'Parts',
                        label: translations.PARTS,
                        myJdPermissions: MANAGE_EQUIPMENT_SETTINGS
                    },
                    {
                        element: <PartTypesSettings/>,
                        id: 'PartTypes',
                        label: translations.ONLINK_PART_TYPES,
                        myJdPermissions: MANAGE_EQUIPMENT_SETTINGS
                    }
                ],
                title: translations.ONLINK_FLEET_SETTINGS
            },
            icon: <IconEquipmentTurf {...ICON_STYLE}/>,
            tileName: 'Fleet'
        })
    ];
}

async function getAvailableSettings(translations, membership, appKeys, permissions, myJdPermissions) {
    const userAuth = {
        appKeys,
        isMigrated: membership.isMigrated,
        myJdPermissions,
        permissions
    };

    const settings = await getSettingsTiles(translations, membership);
    const filteredSettings = settings.filter((tile) => {
        const authorized = isAuthorized(tile, userAuth);

        if (authorized) {
            tile.dialogProps.content = tile.dialogProps.content.filter((tab) =>
                isAuthorized(tab, userAuth) && (isNullOrUndefined(tab.show) || tab.show)
            );
        }

        return authorized;
    });

    return filteredSettings;
}

export {
    getAvailableSettings
};
